import React, {useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import PreferencesAlertsPanel from "./PreferencesAlertsPanel";
import { FileDown, FileText, Play, X, Trash2 } from "lucide-react";

type SimpleItem = { id: number; name: string; visible: boolean; isnew: boolean };
type ExchangeItem = {
    id: number; name: string; visible: boolean;
    isetsnew: boolean; isetsupdate: boolean; isdocumentnew: boolean; isdocumentupdate: boolean;
};
type ExchangeAlertItem = {
    id: number; description: string; link?: string | null; date: string;
    vendorName: string; categoryName: string; visible: boolean; isnew: boolean;
};
type UserPrefAlertEmail = {
    id: number; name: string; visible: boolean;
    vendors: SimpleItem[]; products: SimpleItem[]; exchanges: ExchangeItem[]; exchangeAlerts: ExchangeAlertItem[];
};
type TriggeredAlertDateGroup = {
    id: string; name: string; visible: boolean; userpreferencesalertsemails: UserPrefAlertEmail[];
};

type SavedSearchItem = {
    id: number; name: string; userid: number; user: string; shared: boolean; visible: boolean;
    savedComparion3di: string; savedComparionBw: string;
    savedComparion3dinocomma: string; savedComparionBwnocomma: string; comparisonTypeID?: number;
};

type SavedSearchBuckets = {
    productcomparisonnoshare: SavedSearchItem[];
    productcomparisonshare: SavedSearchItem[];
    productcomparisonfct: SavedSearchItem[];
    productcomparisonrisk?: SavedSearchItem[];
    productcomparisonvendorcategory?: SavedSearchItem[];
    productcomparisonproductcategory: SavedSearchItem[];
    productcomparisonproductcombination: SavedSearchItem[];
    productcomparisonkeyword: SavedSearchItem[];
    productcomparisoninstrument?: SavedSearchItem[];
};

type MyProfilerResponse = {
    userid: number; companyid: number; userfullname: string; company: string;
    totalnumberofpagesTriggedalert: number; numberofdatesperpage: number;
    datesAndUserpreferencesalertsemail: TriggeredAlertDateGroup[];
    savedsearch: SavedSearchBuckets;
};

type SavedSearchPayload = SavedSearchBuckets | { savedsearch: SavedSearchBuckets };
const extractBuckets = (p: SavedSearchPayload): SavedSearchBuckets =>
    (p as any)?.savedsearch ?? (p as SavedSearchBuckets);

const parseIdList = (raw: string | null): number[] =>
    (raw || "")
        .split(/[,\s]+/)
        .map((s) => parseInt(s, 10))
        .filter((n) => Number.isFinite(n));

const parseKeywordTerms = (raw: string | null): string[] =>
    (raw || "")
        .split(",")
        .map((term) => term.trim())
        .filter(Boolean);

const parseFeatureIdsByModule = (raw: string | null): Record<number, number[]> => {
    const grouped: Record<number, number[]> = {};
    (raw || "")
        .split(";")
        .map((group) => group.trim())
        .filter(Boolean)
        .forEach((group) => {
            const [modulePart, idsPart] = group.split(":");
            const moduleId = parseInt((modulePart || "").trim(), 10);
            const ids = parseIdList(idsPart || "");
            if (Number.isFinite(moduleId) && ids.length) grouped[moduleId] = ids;
        });
    return grouped;
};

type Props = {
    apiBase?: string;
    mode?: string;
    onOpenVendorTearsheet?: (id: number) => void;
    onOpenProductTearsheet?: (id: number) => void;
    onOpenDataModal?: (productIds: string | number) => void;
};

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2 className="text-xl font-semibold text-[var(--my-profiler-accent)] mb-3">{children}</h2>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="rounded-2xl shadow p-4 bg-white border">{children}</div>
);

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="grid grid-cols-12 py-2 border-b last:border-b-0">
        <div className="col-span-3 font-medium">{label}</div>
        <div className="col-span-9">{value}</div>
    </div>
);

const SavedSearchTable: React.FC<{
    title: string;
    items?: SavedSearchItem[];
    showAuthor?: boolean;
    onRun?: (row: SavedSearchItem) => void;
    currentUserId?: number | null;
    onDelete?: (row: SavedSearchItem) => void;
}> = ({ title, items, showAuthor, onRun, currentUserId, onDelete }) => {
    const list = items ?? [];
    if (!list.length) return null;
    return (
        <Card>
            <div className="font-semibold mb-2 text-[#25408f]">{title}</div>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                    <thead>
                    <tr className="bg-gray-50">
                        <th className="text-left p-2">Action</th>
                        <th className="text-left p-2 w-2/3">Comparison Name</th>
                        {showAuthor && <th className="text-left p-2">Author</th>}
                        <th className="text-left p-2">Shared</th>
                    </tr>
                    </thead>
                    <tbody>
                    {list.map((row) => (
                        <tr key={row.id} className="border-t">
                            <td className="p-2">
                                <div className="flex items-center gap-2">
                                    <button
                                        className="p-2 rounded hover:bg-muted"
                                        onClick={() => onRun?.(row)}
                                        title="Run saved search"
                                        aria-label="Run saved search"
                                    >
                                        <Play className="h-4 w-4 text-[#25408f]" />
                                    </button>

                                    {currentUserId != null && row.userid === currentUserId && (
                                        <button
                                            className="p-2 rounded hover:bg-red-50"
                                            onClick={() => onDelete?.(row)}
                                            title="Delete saved search"
                                            aria-label="Delete saved search"
                                        >
                                            <Trash2 className="h-4 w-4 text-red-600" />
                                        </button>
                                    )}
                                </div>
                            </td>
                            <td className="p-2">{row.name}</td>
                            {showAuthor && <td className="p-2">{row.user || "—"}</td>}
                            <td className="p-2">{row.shared ? "Yes" : "No"}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
};

const authHeaders = () => {
    const t = localStorage.getItem("token");
    return t ? { Authorization: `Bearer ${t}` } : {};
};

const normalizeIds = (s?: string) =>
    (s || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .join(",");

const extractSavedQuery = (row: SavedSearchItem): string => {
    const q =
        (row?.savedComparion3di ?? "").trim() ||
        (row?.savedComparion3dinocomma ?? "").trim() ||
        (row?.savedComparionBw ?? "").trim() ||
        (row?.savedComparionBwnocomma ?? "").trim();

    return q;
};

const AlertsModal: React.FC<{
    group: TriggeredAlertDateGroup | null;
    onClose: () => void;
    accent: string;
}> = ({ group, onClose, accent }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    if (!group) return null;

    const escapeHtml = (s: string) =>
        s.replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const slugify = (s: string) =>
        s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "alerts";

    const flagsStr = (x: ExchangeItem) => [
        x.isetsnew ? "ETS new" : "",
        x.isetsupdate ? "ETS updated" : "",
        x.isdocumentnew ? "Docs new" : "",
        x.isdocumentupdate ? "Docs updated" : "",
    ].filter(Boolean).join(", ");

    const buildPrintHtml = () => {
        const inner = contentRef.current?.innerHTML ?? "";
        const safeTitle = escapeHtml(group.name);
        return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${safeTitle} - Alerts</title>
<style>
  @page { size: A4; margin: 16mm; }
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #111; }
  h1 { font-size: 20px; margin: 0 0 12px; color:${accent}; }
  .kicker { font-size: 12px; color:#555; margin-bottom: 16px; }
  .card { border: 1px solid #e5e7eb; border-radius: 12px; margin: 12px 0; }
  .card-h { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; font-weight: 600; background:#f9fafb; }
  .card-b { padding: 12px; }
  ul { margin-left: 18px; }
  a { word-break: break-all; }
  .no-print { display: none !important; }
</style>
</head>
<body>
  <h1>${safeTitle}</h1>
  <div class="kicker">Exported from My Triggered Alerts</div>
  ${inner}
</body>
</html>`;
    };

    const handleExportPdf = async () => {
        const { jsPDF } = await import("jspdf");

        if (!group) return;
        const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

        // Layout
        const pageW = pdf.internal.pageSize.getWidth();
        const pageH = pdf.internal.pageSize.getHeight();
        const margin = 12;
        const contentW = pageW - margin * 2;
        let x = margin;
        let y = margin;

        // helpers
        const line = (text = "", opts?: { bold?: boolean; size?: number }) => {
            const size = opts?.size ?? 11;
            pdf.setFont("helvetica", opts?.bold ? "bold" : "normal");
            pdf.setFontSize(size);
            const wrapped = pdf.splitTextToSize(text, contentW);
            const lineHeight = size * 0.42 + 3; // readable spacing
            wrapped.forEach((ln: string) => {
                if (y + lineHeight > pageH - margin) { pdf.addPage(); y = margin; }
                pdf.text(ln, x, y);
                y += lineHeight;
            });
        };

        const spacer = (h = 4) => {
            if (y + h > pageH - margin) { pdf.addPage(); y = margin; }
            y += h;
        };

        const bulletList = (items: string[]) => {
            const bulletX = x + 2.5;
            const textX = x + 6.5;
            items.forEach((item) => {
                const wrapped = pdf.splitTextToSize(item, contentW - (textX - x));
                const lineHeight = 11 * 0.42 + 3;
                if (y + lineHeight > pageH - margin) { pdf.addPage(); y = margin; }
                pdf.circle(bulletX, y - 2.2, 0.6, "F");
                wrapped.forEach((ln: string, idx: number) => {
                    if (idx > 0 && y + lineHeight > pageH - margin) { pdf.addPage(); y = margin; }
                    pdf.text(ln, textX, y);
                    y += lineHeight;
                });
            });
        };

        // Title
        line(group.name, { bold: true, size: 16 });
        spacer(2);
        line("Exported from My Triggered Alerts", { size: 9 });
        spacer(4);

        group.userpreferencesalertsemails.forEach((email, i) => {
            if (i > 0) { spacer(6); }
            line(email.name, { bold: true, size: 13 });
            spacer(2);

            // Vendors
            line("Vendors", { bold: true });
            if (email.vendors?.length) {
                bulletList(email.vendors.map(v => `${v.name}${v.isnew ? " (new)" : ""}`));
            } else {
                line("—");
            }
            spacer(2);

            // Products
            line("Products", { bold: true });
            if (email.products?.length) {
                bulletList(email.products.map(p => `${p.name}${p.isnew ? " (new)" : ""}`));
            } else {
                line("—");
            }
            spacer(2);

            // Exchanges
            line("Exchanges", { bold: true });
            if (email.exchanges?.length) {
                bulletList(email.exchanges.map(e => {
                    const f = flagsStr(e);
                    return f ? `${e.name} [${f}]` : e.name;
                }));
            } else {
                line("—");
            }
            spacer(2);

            // Exchange Alerts
            line("Exchange Alerts", { bold: true });
            if (email.exchangeAlerts?.length) {
                bulletList(email.exchangeAlerts.map(a =>
                    `${a.description}${a.link ? ` (${a.link})` : ""} — ${a.vendorName} / ${a.categoryName} (${a.date})`
                ));
            } else {
                line("—");
            }
        });

        const filename = `${slugify(group.name)}-alerts.pdf`;
        pdf.save(filename);
    };

    const handleExportWord = () => {
        const html = buildPrintHtml();
        const blob = new Blob(["\ufeff", html], { type: "application/msword" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slugify(group.name)}-alerts.doc`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="alerts-modal-title"
            className="fixed inset-0 z-[999] flex items-center justify-center"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50" />

            {/* Modal card */}
            <div
                className="relative z-10 w-[min(1000px,92vw)] max-h-[80vh] overflow-hidden rounded-2xl bg-white shadow-xl border"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b bg-gray-50">
                    <h3 id="alerts-modal-title" className="text-lg font-semibold" style={{ color: accent }}>
                        {group.name}
                    </h3>
                    <div className="flex items-center gap-2" role="toolbar" aria-label="Export actions">
                        {/* PDF */}
                        <button
                            type="button"
                            onClick={handleExportPdf}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border hover:bg-white"
                            style={{ borderColor: accent, color: accent }}
                            title="Export as PDF"
                            aria-label="Export as PDF"
                        >
                            <FileDown className="w-5 h-5" aria-hidden="true" />
                        </button>

                        {/* Word */}
                        <button
                            type="button"
                            onClick={handleExportWord}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border hover:bg-white"
                            style={{ borderColor: accent, color: accent }}
                            title="Export as Word (.docx)"
                            aria-label="Export as Word (.docx)"
                        >
                            <FileText className="w-5 h-5" aria-hidden="true" />
                        </button>

                        {/* Close */}
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex items-center justify-center w-10 h-10 rounded-full border hover:bg-white"
                            style={{ borderColor: accent, color: accent }}
                            title="Close"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" aria-hidden="true" />
                        </button>
                    </div>
                </div>

                <div
                    ref={contentRef}
                    className="p-4 overflow-y-auto max-h-[calc(80vh-56px)] space-y-4"
                >
                    {group.userpreferencesalertsemails.map((up) => (
                        <div key={up.id} className="border rounded-lg card">
                            <div className="px-3 py-2 font-medium border-b card-h">{up.name}</div>
                            <div className="p-3 space-y-2 card-b">
                                <Row
                                    label="Vendors"
                                    value={
                                        up.vendors.length ? (
                                            <ul className="list-disc ml-5">
                                                {up.vendors.map((v) => (
                                                    <li key={v.id}>
                                                        {v.name}
                                                        {v.isnew ? " (new)" : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span className="text-gray-500">—</span>
                                        )
                                    }
                                />
                                <Row
                                    label="Products"
                                    value={
                                        up.products.length ? (
                                            <ul className="list-disc ml-5">
                                                {up.products.map((p) => (
                                                    <li key={p.id}>
                                                        {p.name}
                                                        {p.isnew ? " (new)" : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span className="text-gray-500">—</span>
                                        )
                                    }
                                />
                                <Row
                                    label="Exchanges"
                                    value={
                                        up.exchanges.length ? (
                                            <ul className="list-disc ml-5">
                                                {up.exchanges.map((x) => (
                                                    <li key={x.id}>
                                                        {x.name}
                                                        {x.isetsnew ? " [ETS new]" : ""}
                                                        {x.isetsupdate ? " [ETS updated]" : ""}
                                                        {x.isdocumentnew ? " [Docs new]" : ""}
                                                        {x.isdocumentupdate ? " [Docs updated]" : ""}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span className="text-gray-500">—</span>
                                        )
                                    }
                                />
                                <Row
                                    label="Exchange Alerts"
                                    value={
                                        up.exchangeAlerts.length ? (
                                            <ul className="list-disc ml-5 break-words">
                                                {up.exchangeAlerts.map((ea) => (
                                                    <li key={ea.id}>
                                                        {ea.link ? (
                                                            <a
                                                                className="underline"
                                                                href={ea.link}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                {ea.description}
                                                            </a>
                                                        ) : (
                                                            ea.description
                                                        )}
                                                        {" — "}
                                                        {ea.vendorName} / {ea.categoryName} ({ea.date})
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span className="text-gray-500">—</span>
                                        )
                                    }
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

const MyProfiler: React.FC<Props> = ({ apiBase = "", mode = "profiler", onOpenDataModal }) => {
    const resolvedMode = String(mode || "").trim().toLowerCase() === "altdata" ? "altdata" : "profiler";
    const accent = resolvedMode === "altdata" ? "var(--altdata-red, #990026)" : "#E87722";
    const profilerUrl = `${apiBase}/profiler?mode=${resolvedMode}`;
    const savedSearchesUrl = `${apiBase}/saved-searches?mode=${resolvedMode}`;
    // Alerts + (fallback) saved from /profiler
    const [data, setData] = useState<MyProfilerResponse | null>(null);
    // Saved only from /saved-searches
    const [savedOnly, setSavedOnly] = useState<SavedSearchBuckets | null>(null);

    // 1=createdByMe, 2=createdByMeAndShared, 3=companyShared, 4=all
    const [viewFilter, setViewFilter] = useState<1 | 2 | 3 | 4>(4);
    const [nameFilter, setNameFilter] = useState("");
    const [activeTab, setActiveTab] = useState<'prefs' | 'alerts' | 'saved'>('prefs');

    const [openGroup, setOpenGroup] = useState<TriggeredAlertDateGroup | null>(null);

    const lastRunPayloadRef = React.useRef({
        productCategory: { ids: [], names: [] },
        vendorCategory:  { ids: [], names: [] },
        features:        { ids: [], names: [] },
    });

    const uid = data?.userid ?? null;

    const removeFromBuckets = (b: SavedSearchBuckets, removeId: number): SavedSearchBuckets => {
        const prune = (arr?: SavedSearchItem[]) => (arr ?? []).filter(x => x.id !== removeId);
        return {
            productcomparisonnoshare: prune(b.productcomparisonnoshare),
            productcomparisonshare: prune(b.productcomparisonshare),
            productcomparisonfct: prune(b.productcomparisonfct),
            productcomparisonrisk: prune(b.productcomparisonrisk),
            productcomparisonvendorcategory: prune(b.productcomparisonvendorcategory),
            productcomparisonproductcategory: prune(b.productcomparisonproductcategory),
            productcomparisonproductcombination: prune(b.productcomparisonproductcombination),
            productcomparisonkeyword: prune(b.productcomparisonkeyword),
            productcomparisoninstrument: prune(b.productcomparisoninstrument),
        };
    };

    const handleDelete = async (row: SavedSearchItem) => {
        try {
            await axios.delete(`${apiBase}/saved-searches/${row.id}`, { headers: authHeaders() });

            setSavedOnly(prev => (prev ? removeFromBuckets(prev, row.id) : prev));
            setData(prev => (prev ? { ...prev, savedsearch: removeFromBuckets(prev.savedsearch, row.id) } : prev));
        } catch (err: any) {
            const msg = err?.response?.data?.message || "Failed to delete saved search.";
            alert(msg);
        }
    };


    useEffect(() => {
        const onRunProd = (e) => {
            const ids   = (e?.detail?.categoryItemIds ?? []).map(Number).filter(Number.isFinite);
            const names = Array.isArray(e?.detail?.categoryItemNames) ? e.detail.categoryItemNames : [];
            lastRunPayloadRef.current.productCategory = { ids, names };
        };
        const onRunVend = (e) => {
            const ids   = (e?.detail?.categoryItemIds ?? []).map(Number).filter(Number.isFinite);
            const names = Array.isArray(e?.detail?.categoryItemNames) ? e.detail.categoryItemNames : [];
            lastRunPayloadRef.current.vendorCategory = { ids, names };
        };
        const onRunFeat = (e) => {
            const ids   = (e?.detail?.featureIds ?? []).map(Number).filter(Number.isFinite);
            const names = Array.isArray(e?.detail?.featureNames) ? e.detail.featureNames : [];
            lastRunPayloadRef.current.features = { ids, names };
        };

        window.addEventListener("profiler:run-product-category-search", onRunProd);
        window.addEventListener("profiler:run-vendor-category-search", onRunVend);
        window.addEventListener("profiler:run-feature-search", onRunFeat);
        return () => {
            window.removeEventListener("profiler:run-product-category-search", onRunProd);
            window.removeEventListener("profiler:run-vendor-category-search", onRunVend);
            window.removeEventListener("profiler:run-feature-search", onRunFeat);
        };
    }, []);


    // Fetch alerts + base info
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get<MyProfilerResponse>(profilerUrl, { headers: authHeaders() });
                if (!cancelled) setData(res.data);
            } catch (err: any) {
                if (err?.response) {
                    const ct = err.response.headers?.["content-type"] || "";
                    const snippet =
                        typeof err.response.data === "string"
                            ? err.response.data.slice(0, 200)
                            : JSON.stringify(err.response.data).slice(0, 200);
                    console.error(`GET ${profilerUrl} -> HTTP ${err.response.status} ${ct}`, snippet);
                } else {
                    console.error(err);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [profilerUrl]);

    // Fetch saved-only buckets
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get<SavedSearchPayload>(savedSearchesUrl, { headers: authHeaders() });
                if (!cancelled) setSavedOnly(extractBuckets(res.data));
            } catch (err: any) {
                if (err?.response) {
                    const ct = err.response.headers?.["content-type"] || "";
                    const snippet =
                        typeof err.response.data === "string"
                            ? err.response.data.slice(0, 200)
                            : JSON.stringify(err.response.data).slice(0, 200);
                    console.error(`GET ${savedSearchesUrl} -> HTTP ${err.response.status} ${ct}`, snippet);
                } else {
                    console.error(err);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [savedSearchesUrl]);

    // Pager for alerts
    const pages = useMemo(() => {
        if (!data) return [];
        const size = data.numberofdatesperpage;
        const arr = data.datesAndUserpreferencesalertsemail;
        const chunks: TriggeredAlertDateGroup[][] = [];
        for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
        return chunks;
    }, [data]);
    const [pageIndex, setPageIndex] = useState(0);

    const savedBuckets = savedOnly ?? data?.savedsearch ?? null;

    // Apply filters safely even if /profiler (userid) isn’t loaded yet
    const savedBucketsFiltered = useMemo(() => {
        if (!savedBuckets) return null;

        const uid = data?.userid ?? null;
        const vf: 1 | 2 | 3 | 4 = uid == null ? 4 : viewFilter;

        const apply = (items?: SavedSearchItem[]) =>
            (items ?? [])
                .filter((it) => {
                    if (vf === 1) return uid != null && it.userid === uid && !it.shared;
                    if (vf === 2) return uid != null && it.userid === uid && it.shared;
                    if (vf === 3) return uid != null && it.userid !== uid && it.shared;
                    return true; // 4=all or uid unknown
                })
                .filter((it) => !nameFilter || it.name.toLowerCase().includes(nameFilter.toLowerCase()));

        return {
            productcomparisonnoshare: apply(savedBuckets.productcomparisonnoshare),
            productcomparisonshare: apply(savedBuckets.productcomparisonshare),
            productcomparisonfct: apply(savedBuckets.productcomparisonfct),
            productcomparisonrisk: apply(savedBuckets.productcomparisonrisk),
            productcomparisonvendorcategory: apply(savedBuckets.productcomparisonvendorcategory),
            productcomparisonproductcategory: apply(savedBuckets.productcomparisonproductcategory),
            productcomparisonproductcombination: apply(savedBuckets.productcomparisonproductcombination),
            productcomparisonkeyword: apply(savedBuckets.productcomparisonkeyword),
            productcomparisoninstrument: apply(savedBuckets.productcomparisoninstrument),
        };
    }, [savedBuckets, data?.userid, viewFilter, nameFilter]);

    return (
        <div
            className="p-6 min-h-[280px] h-[550px] flex flex-col overflow-hidden"
            style={{ "--my-profiler-accent": accent } as React.CSSProperties}
        >
            {/* Tabs header */}
            <div
                className="flex gap-2 border-b mb-4 shrink-0"
                role="tablist"
                aria-label="My Profiler Sections"
            >
                {[
                    { id: 'prefs', label: 'Preferences & Alerts' },
                    { id: 'alerts', label: 'My Triggered Alerts' },
                    { id: 'saved', label: 'My Saved Searches' },
                ].map((t) => (
                    <button
                        key={t.id}
                        role="tab"
                        aria-selected={activeTab === t.id}
                        aria-controls={`panel-${t.id}`}
                        className={[
                            "px-4 py-2 rounded-t-lg border",
                            activeTab === t.id
                                ? "bg-white border-b-white font-semibold"
                                : "bg-gray-50 text-gray-700 hover:bg-white"
                        ].join(" ")}
                        style={activeTab === t.id ? { color: accent, borderColor: accent } : undefined}
                        onClick={() => setActiveTab(t.id as typeof activeTab)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Scrollable panels area */}
            <div className="flex-1 min-h-0 overflow-auto pr-1">
                {/* --- Tab: Preferences & Alerts --- */}
                {activeTab === 'prefs' && (
                    <div
                        id="panel-prefs"
                        role="tabpanel"
                        aria-labelledby="prefs"
                        className="space-y-4"
                    >
                            <PreferencesAlertsPanel apiBase={apiBase} mode={resolvedMode} />
                    </div>
                )}

                {/* --- Tab: My Triggered Alerts --- */}
                {activeTab === 'alerts' && (
                    <div
                        id="panel-alerts"
                        role="tabpanel"
                        aria-labelledby="alerts"
                        className="space-y-4"
                    >
                        {!data ? (
                            <Card>Loading alerts…</Card>
                        ) : (
                            <>

                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                    {(pages[pageIndex] ?? []).map((group) => {
                                        const rollup = group.userpreferencesalertsemails.reduce(
                                            (acc, u) => {
                                                acc.v += u.vendors.length;
                                                acc.p += u.products.length;
                                                acc.x += u.exchanges.length;
                                                acc.a += u.exchangeAlerts.length;
                                                return acc;
                                            },
                                            { v: 0, p: 0, x: 0, a: 0 }
                                        );

                                        return (
                                            <button
                                                key={group.id}
                                                onClick={() => setOpenGroup(group)}
                                                className="text-left rounded-2xl border shadow hover:shadow-md transition p-4 bg-white focus:outline-none focus:ring-2 focus:ring-[#25408f]"
                                                aria-label={`Open alerts for ${group.name}`}
                                            >
                                                <div className="text-lg font-semibold" style={{ color: accent }}>{group.name}</div>
                                                <div className="mt-2 text-sm text-gray-700">
                                                    <div className="flex flex-wrap gap-3">
                                                        <span className="inline-flex items-center gap-1">
                      <span className="font-medium">{rollup.v}</span> vendors
                    </span>
                                                        <span className="inline-flex items-center gap-1">
                      <span className="font-medium">{rollup.p}</span> products
                    </span>
                                                        <span className="inline-flex items-center gap-1">
                      <span className="font-medium">{rollup.x}</span> exchanges
                    </span>
                                                        <span className="inline-flex items-center gap-1">
                      <span className="font-medium">{rollup.a}</span> exchange alerts
                    </span>
                                                    </div>
                                                    <div className="mt-1 text-xs text-gray-500">Click to view details</div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Pager */}
                                <div className="sticky top-0 z-10 bg-white/90 backdrop-blur py-2">
                                    <div className="flex w-full items-center justify-center gap-3">
                                        <button
                                            className="px-3 py-1 border rounded"
                                            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                                            disabled={pageIndex === 0}
                                        >
                                            Prev
                                        </button>
                                        <div>Page {pageIndex + 1} / {Math.max(1, pages.length)}</div>
                                        <button
                                            className="px-3 py-1 border rounded"
                                            onClick={() => setPageIndex((p) => Math.min(pages.length - 1, p + 1))}
                                            disabled={pageIndex >= pages.length - 1}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>

                                {/* Modal */}
                                <AlertsModal group={openGroup} onClose={() => setOpenGroup(null)} accent={accent} />
                            </>
                        )}
                    </div>
                )}


                {/* --- Tab: My Saved Searches --- */}
                {activeTab === 'saved' && (
                    <div
                        id="panel-saved"
                        role="tabpanel"
                        aria-labelledby="saved"
                        className="space-y-4"
                    >
                        <Card>
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <label><input type="radio" name="view" checked={viewFilter === 1} onChange={() => setViewFilter(1)} /> Created by me</label>
                                    <label><input type="radio" name="view" checked={viewFilter === 2} onChange={() => setViewFilter(2)} /> Created by me &amp; shared</label>
                                    <label><input type="radio" name="view" checked={viewFilter === 3} onChange={() => setViewFilter(3)} /> Company shared</label>
                                    <label><input type="radio" name="view" checked={viewFilter === 4} onChange={() => setViewFilter(4)} /> All</label>
                                </div>
                                <input
                                    className="border rounded px-3 py-1"
                                    placeholder="Filter by name"
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                />
                            </div>
                        </Card>

                        {!savedBucketsFiltered ? (
                            <Card>Loading saved searches…</Card>
                        ) : (
                            <>
                                <SavedSearchTable
                                    title="Combination Search"
                                    items={savedBucketsFiltered.productcomparisonproductcombination}
                                    currentUserId={uid}
                                    onDelete={handleDelete}
                                    onRun={(row) => {
                                        const qs = extractSavedQuery(row);
                                        //console.log(" qs ", qs);
                                        if (!qs) return;

                                        const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);

                                        const keyword =
                                            (params.get("keyword") || params.get("query") || "").trim();
                                        const productCategoryItemIds = parseIdList(
                                            params.get("categoryItemIds") || params.get("categoryItemsIds")
                                        );
                                        const vendorCategoryItemIds = parseIdList(
                                            params.get("vendorCategoryItemIds")
                                        );
                                        const featureIds = parseIdList(params.get("featureIds"));
                                        const featureIdsByModule = parseFeatureIdsByModule(
                                            params.get("featureIdsByModule")
                                        );
                                        const keywordTerms = parseKeywordTerms(keyword);

                                        if (
                                            !keywordTerms.length &&
                                            !productCategoryItemIds.length &&
                                            !vendorCategoryItemIds.length &&
                                            !featureIds.length
                                        ) return;

                                        window.dispatchEvent(
                                            new CustomEvent("profiler:run-combination-search", {
                                                detail: {
                                                    keyword: keywordTerms[0] || keyword,
                                                    keywords: keywordTerms.slice(1),
                                                    productCategoryItemIds,
                                                    vendorCategoryItemIds,
                                                    featureIds,
                                                    featureIdsByModule,
                                                    source: "my-profiler",
                                                    mode: "discover",
                                                    match: "all",
                                                    type: "combination",
                                                },
                                            })
                                        );
                                    }}
                                />
                                <SavedSearchTable
                                    title="Unshared Product Comparisons"
                                    items={savedBucketsFiltered.productcomparisonnoshare}
                                    currentUserId={uid}
                                    onDelete={handleDelete}
                                    onRun={(row) => {
                                        const ids = normalizeIds(row.savedComparion3di) || normalizeIds(row.savedComparionBw);
                                        if (!ids) {
                                            console.warn("No product IDs found in saved comparison", row);
                                            return;
                                        }
                                        onOpenDataModal?.(ids);
                                    }}
                                />
                                <SavedSearchTable
                                    title="Product Comparisons For Company"
                                    items={savedBucketsFiltered.productcomparisonshare}
                                    showAuthor
                                    currentUserId={uid}
                                    onDelete={handleDelete}
                                    onRun={(row) => {
                                        const ids = normalizeIds(row.savedComparion3di) || normalizeIds(row.savedComparionBw);
                                        if (!ids) {
                                            console.warn("No product IDs found in saved comparison", row);
                                            return;
                                        }
                                        onOpenDataModal?.(ids);
                                    }}
                                />
                                <SavedSearchTable
                                    title="Data & Functionality Search"
                                    items={savedBucketsFiltered.productcomparisonfct}
                                    currentUserId={uid}
                                    onDelete={handleDelete}
                                    onRun={(row) => {
                                        const qs =
                                            (row?.savedComparion3di ?? "").trim() ||
                                            (row?.savedComparion3dinocomma ?? "").trim() ||
                                            (row?.savedComparionBw ?? "").trim() ||
                                            (row?.savedComparionBwnocomma ?? "").trim();

                                        if (!qs) return;

                                        const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);

                                        const raw = params.get("featureIds") || "";
                                        const ids = raw
                                            .split(/[,\s]+/)
                                            .map((s) => parseInt(s, 10))
                                            .filter((n) => Number.isFinite(n));

                                        if (!ids.length) return;

                                        window.dispatchEvent(
                                            new CustomEvent("profiler:run-feature-search", {
                                                detail: { featureIds: ids, source: "my-profiler" },
                                            })
                                        );
                                    }}
                                />
                                {!!savedBucketsFiltered.productcomparisonrisk?.length && (
                                    <SavedSearchTable
                                        title="Vendor Category Risk Search"
                                        items={savedBucketsFiltered.productcomparisonrisk}
                                    />
                                )}
                                {!!savedBucketsFiltered.productcomparisonvendorcategory?.length && (
                                    <SavedSearchTable
                                        title="Vendor Categorisation Search"
                                        items={savedBucketsFiltered.productcomparisonvendorcategory}
                                        currentUserId={uid}
                                        onDelete={handleDelete}
                                        onRun={(row) => {
                                            const qs =
                                                (row?.savedComparion3di ?? "").trim() ||
                                                (row?.savedComparion3dinocomma ?? "").trim() ||
                                                (row?.savedComparionBw ?? "").trim() ||
                                                (row?.savedComparionBwnocomma ?? "").trim();

                                            if (!qs) return;

                                            const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);

                                            const raw =
                                                params.get("vendorCategoryItemIds") ||
                                                params.get("categoryItemsIds") ||
                                                params.get("categoryItemIds") ||
                                                "";

                                            const ids = raw
                                                .split(/[,\s]+/)
                                                .map((s) => parseInt(s, 10))
                                                .filter((n) => Number.isFinite(n));

                                            if (!ids.length) return;

                                            window.dispatchEvent(
                                                new CustomEvent("profiler:run-vendor-category-search", {
                                                    detail: { categoryItemIds: ids, source: "my-profiler" },
                                                })
                                            );
                                        }}
                                    />
                                )}
                                <SavedSearchTable
                                    title="Product Categorisation Search"
                                    items={savedBucketsFiltered.productcomparisonproductcategory}
                                    currentUserId={uid}
                                    onDelete={handleDelete}
                                    onRun={(row) => {
                                        const qs =
                                            (row?.savedComparion3di ?? "").trim() ||
                                            (row?.savedComparion3dinocomma ?? "").trim() ||
                                            (row?.savedComparionBw ?? "").trim() ||
                                            (row?.savedComparionBwnocomma ?? "").trim();

                                        if (!qs) return;

                                        const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);

                                        const raw =
                                            params.get("categoryItemsIds") ||
                                            params.get("categoryItemIds") ||
                                            "";

                                        const ids = raw
                                            .split(/[,\s]+/)
                                            .map((s) => parseInt(s, 10))
                                            .filter((n) => Number.isFinite(n));

                                        if (!ids.length) return;

                                        window.dispatchEvent(
                                            new CustomEvent("profiler:run-product-category-search", {
                                                detail: { categoryItemIds: ids, source: "my-profiler" },
                                            })
                                        );
                                    }}
                                />
                                <SavedSearchTable
                                    title="Vendor, Product or Keyword Search"
                                    items={savedBucketsFiltered.productcomparisonkeyword}
                                    currentUserId={uid}
                                    onDelete={handleDelete}
                                    onRun={(row) => {
                                        const qs = extractSavedQuery(row);
                                        const params = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);
                                        const savedKeyword =
                                            (params.get("txtKeyword") ||
                                                params.get("keyword") ||
                                                row?.name ||
                                                "").trim();
                                        const keywordTerms = parseKeywordTerms(savedKeyword);
                                        const kw = keywordTerms[0] || savedKeyword;
                                        if (!kw) return;

                                        const ctype = row.comparisonTypeID ?? row.comparisontypeid;

                                        //console.log("Saved keyword search row", row, "ctype=", ctype);

                                        // default
                                        let mode: "discover" | "vendor" | "product" = "discover";
                                        let match: "any" | "all" | "exact" = "all";

                                        if (ctype === 5 || ctype === 17) {
                                            // Vendor Name
                                            mode = "vendor";
                                            match = "exact";
                                        } else if (ctype === 6) {
                                            // Product Name
                                            mode = "product";
                                            match = "exact";
                                        } else if (ctype === 7 || ctype === 18) {
                                            // Keyword Any
                                            mode = "discover";
                                            match = "any";
                                        } else if (ctype === 8 || ctype === 19) {
                                            // Keyword All
                                            mode = "discover";
                                            match = "all";
                                        } else if (ctype === 9 || ctype === 20) {
                                            // Keyword Exact
                                            mode = "discover";
                                            match = "exact";
                                        }

                                        //console.log("mode", mode, "match=", match);

                                        window.dispatchEvent(
                                                new CustomEvent("profiler:run-keyword-search", {
                                                detail: {
                                                    keyword: kw,
                                                    keywords: keywordTerms.slice(1),
                                                    source: "my-profiler",
                                                    mode,
                                                    match,
                                                },
                                            })
                                        );
                                    }}
                                />
                                {!!savedBucketsFiltered.productcomparisoninstrument?.length && (
                                    <SavedSearchTable
                                        title="Instrument Code or Name"
                                        items={savedBucketsFiltered.productcomparisoninstrument}
                                        currentUserId={uid}
                                        onDelete={handleDelete}
                                        onRun={(row) => {
                                            const kw = (row?.name ?? "").trim();
                                            if (!kw) return;
                                            window.dispatchEvent(new CustomEvent("profiler:run-keyword-search", {
                                                detail: { keyword: kw, source: "my-profiler" }
                                            }));
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

};

export default MyProfiler;
